'use client';

import React, { useMemo } from 'react';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { useDashboard } from 'src/context/providers/DashboardProvider';
import { PulseButton, PulseIconButton, Tooltip } from 'src/components';
import { useSettings } from 'src/components/Settings/hooks';
import { useUserDeactivationRails } from 'src/hooks/authorization';
interface DeactivateButtonProps {
  selectedUser: MedScout.User;
  variant?: 'regular' | 'compact' | 'icon';
}

const DeactivateButton = ({
  selectedUser,
  variant = 'regular',
}: DeactivateButtonProps) => {
  const { setShowConfirmDeleteModal } = useDashboard();
  const { setSelectedUser } = useSettings();
  // Self-deactivation and last-admin rails (MKT-384).
  const { isBlocked, reason, isLoading } =
    useUserDeactivationRails(selectedUser);

  const handleOpenDialog = () => {
    // Set the selected user in the settings context
    setSelectedUser(selectedUser);
    // Open the dialog
    setShowConfirmDeleteModal(true);
  };

  const isActive = useMemo(() => {
    if (!selectedUser) return;
    return selectedUser?.is_active;
  }, [selectedUser]);

  // Disabled while the rails are still resolving, so a blocked action is never
  // briefly offered.
  const isDisabled = !selectedUser || isBlocked || isLoading;

  const label = isActive ? 'Deactivate user' : 'Activate user';
  // The reason wins when there is one — it is the actionable message.
  const tooltip = reason ?? label;

  switch (variant) {
    case 'regular': {
      return (
        // Renders children unwrapped when `content` is empty, so an allowed
        // action keeps its original markup.
        <Tooltip content={reason ?? ''}>
          <PulseButton
            variant="outline"
            color={isActive ? 'danger' : 'success'}
            onClick={handleOpenDialog}
            startDecorator={faTrash}
            disabled={isDisabled}
          >
            {isActive ? 'Deactivate' : 'Activate'}
          </PulseButton>
        </Tooltip>
      );
    }
    case 'compact': {
      return (
        <PulseIconButton
          size="sm"
          variant="outline"
          color={isActive ? 'danger' : 'success'}
          onClick={handleOpenDialog}
          aria-label={label}
          icon={faTrash}
          disabled={isDisabled}
          tooltip={tooltip}
        />
      );
    }
    case 'icon': {
      return (
        <PulseIconButton
          size="sm"
          variant="plain"
          onClick={handleOpenDialog}
          icon={faTrash}
          className="text-neutral-500 hover:text-red-500"
          color={isActive ? 'danger' : 'success'}
          disabled={isDisabled}
          tooltip={tooltip}
          aria-label={label}
        />
      );
    }
    default:
      return null;
  }
};

export default DeactivateButton;
